// fetchChatroomMessages.js
'use strict';
import 'isomorphic-fetch';
import parseLinkHeader from 'parse-link-header';

import { MessageMergeBehavior } from '../reducer/messages.js';

import savePosts from '../action/savePosts.js';
import handleFetchError from '../resource/handleFetchError.js';
import getMeData from '../selector/getMeData.js';
import getOperationData from '../selector/getOperationData.js';
import getIsReachRateLimit from '../selector/getIsReachRateLimit.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getListData from '../selector/getListData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import getNextAndPreviousPage from '../resource/getNextAndPreviousPage.js';
import { MIN_CHATROOM_MESSAGES_PAGE_SIZE_LIMIT } from '../resource/chatroomConstants.js';
import {
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Fetch chatroom messages
 * @kind action
 * @param {string} { id } - chatroom id.
 * @param {number} {[page = 1]} - the page number.
 * @param {number} {[limit = 20]} - the page size.
 * @param {boolean} {[shouldResetList = false]} - should reset list.
 * @param {boolean} {[isFetchMediaOnly = false]} - is only fetch media messages
 * @return {Promise} Action promise.
 */
const fetchChatroomMessages =
  ({
    id,
    page = 1,
    limit = MIN_CHATROOM_MESSAGES_PAGE_SIZE_LIMIT,
    shouldResetList = false,
    isFetchMediaOnly = false,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const token = getMeData(state, 'token');

    if (!token) {
      return dispatch({
        type: '',
      });
    }

    const fetchOptions = {
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };

    const currentUnixTimestamp = getCurrentUnixTimestamp();
    const selectPath = isFetchMediaOnly
      ? ['chatroom', id, 'mediaOnly', page]
      : ['chatroom', id, 'messages', page];
    const listSelectPath = isFetchMediaOnly
      ? ['chatroom', id, 'mediaOnly']
      : ['chatroom', id, 'messages'];

    const isReach = getIsReachRateLimit(
      getState(),
      selectPath,
      currentUnixTimestamp
    );

    if (isReach) {
      return dispatch({ type: '' });
    }
    const apiCacheTimestamp = getOperationData(
      state,
      ['chat'],
      'apiCacheTimestamp'
    );
    const url = getResourceUrl({ endpoint: `/chat/${id}/messages` });
    url.searchParams.append('page', page);
    url.searchParams.append('limit', limit);
    url.searchParams.append('_', apiCacheTimestamp);
    if (isFetchMediaOnly) {
      url.searchParams.append('media-only', 1);
    }

    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const totalCount = parseInt(response.headers.get('x-total-count'), 10);
      const links = parseLinkHeader(response.headers.get('Link'));
      const lastPage = links && links.last && parseInt(links.last.page, 10);
      const etag = response.headers.get('etag');

      const data = (await response.json()) || [];
      const messages = data.map(message => ({ ...message, isPending: false }));

      dispatch(
        savePosts({
          posts: messages,
          behavior: MessageMergeBehavior.KEEP_OLD,
        })
      );

      const itemIds = data.map(message => message.id);
      const lastEtag = getNetworkingData(getState(), selectPath, 'etag');
      const isEtagChanged = lastEtag != null && lastEtag !== etag;

      const currentNextPage = getListData(
        getState(),
        listSelectPath,
        'nextPage'
      );
      const currentPreviousPage = getListData(
        getState(),
        listSelectPath,
        'previousPage'
      );

      const { nextPage, previousPage } = getNextAndPreviousPage({
        currentNextPage,
        currentPreviousPage,
        currentPage: page,
        lastPage,
        shouldIgnoreCurrentResult: isEtagChanged || shouldResetList,
      });

      dispatch({
        type:
          shouldResetList || isEtagChanged ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
        payload: {
          selectPath: listSelectPath,
          itemIds,
          totalCount,
          nextPage,
          previousPage,
          lastPage,
          pages: {
            [`${page}`]: itemIds,
          },
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath, etag },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default fetchChatroomMessages;
